import {
  influencerHasLivestreamContent,
  isLivestreamContentType,
} from './campaignContentDeliverables';
import { getInfluencerOfferings } from '../influencer/influencerOfferings';

/**
 * Get missing fields for influencer profile
 */
export function getMissingInfluencerFields(profile) {
  const missing = [];
  const offerings = getInfluencerOfferings(profile);

  // Step 1: Basic Info
  if (!profile.full_name?.trim()) missing.push('full_name');
  if (!profile.tiktok_handle?.trim()) missing.push('tiktok_handle');

  // Step 2: Content Info
  if (offerings.length === 0) {
    missing.push('content_deliverables');
    missing.push('fee_models');
  } else if (offerings.every((offering) => offering.feeModels.length === 0)) {
    missing.push('fee_models');
  }
  if (!profile.content_categories || profile.content_categories.length === 0) {
    missing.push('content_categories');
  }

  // Step 4 (Images): Profile Picture & Portfolio
  if (!profile.profile_picture_url?.trim()) missing.push('profile_picture_url');
  if (!profile.portfolio_images_urls || profile.portfolio_images_urls.length === 0) {
    missing.push('portfolio_images_urls');
  }

  // Step 5: Shipping Address
  if (!profile.street_address?.trim()) missing.push('street_address');
  if (!profile.city?.trim()) missing.push('city');
  if (!profile.state?.trim()) missing.push('state');
  if (!profile.zip_code?.trim()) missing.push('zip_code');

  // Step 6: Offerings pricing
  const hasShortVideo = offerings.some(
    (offering) => !isLivestreamContentType(offering.contentType),
  );
  const hasLivestream = influencerHasLivestreamContent(profile);
  const shortVideoOfferings = offerings.filter(
    (offering) => !isLivestreamContentType(offering.contentType),
  );
  const livestreamOfferings = offerings.filter(
    (offering) => isLivestreamContentType(offering.contentType),
  );

  if (hasShortVideo && !shortVideoOfferings.some((offering) => offering.feeModels.length > 0)) {
    missing.push('short_video_rates');
  }

  if (hasLivestream && !livestreamOfferings.some((offering) => offering.feeModels.length > 0)) {
    missing.push('livestream_rates');
  }

  return missing;
}

/**
 * Get missing fields for brand profile
 */
export function getMissingBrandFields(profile) {
  const missing = [];

  // Step 1: Personal Info
  if (!profile.full_name?.trim()) missing.push('full_name');

  // Step 2: Company Details
  if (!profile.company_name?.trim()) missing.push('company_name');
  if (!profile.company_description?.trim()) missing.push('company_description');

  // Step 3: Campaign Preferences (budget collected later via wallet)
  if (!profile.influencer_categories || profile.influencer_categories.length === 0) {
    missing.push('influencer_categories');
  }
  // Step 5: Brand Images
  if (!profile.brand_logo_url?.trim()) missing.push('brand_logo_url');

  return missing;
}

/**
 * Get user-friendly field names for missing fields
 */
export function getFieldDisplayName(fieldName) {
  const displayNames = {
    // Influencer fields
    full_name: 'Full Name',
    tiktok_handle: 'TikTok Handle',
    content_deliverables: 'Content Types',
    content_categories: 'Content Categories',
    profile_picture_url: 'Profile Picture',
    portfolio_images_urls: 'Portfolio Images',
    street_address: 'Street Address',
    city: 'City',
    state: 'State',
    zip_code: 'ZIP Code',
    fee_models: 'Fee Models',
    short_video_rates: 'Short Video Rates',
    livestream_rates: 'Livestream Rates',

    // Brand fields
    company_name: 'Brand Name',
    company_description: 'Company Description',
    annual_revenue: 'Annual Revenue',
    influencer_categories: 'Preferred Influencer Categories',
    brand_logo_url: 'Brand Logo',
    banner_picture: 'Banner Picture',
  };

  return displayNames[fieldName] || fieldName;
}

/**
 * Get completion percentage
 */
export function getProfileCompletionPercentage(userType, profile) {
  if (!profile) return 0;

  const missingFields = userType === 'influencer'
    ? getMissingInfluencerFields(profile)
    : getMissingBrandFields(profile);

  const totalFields = userType === 'influencer' ? 16 : 10;
  const completedFields = totalFields - missingFields.length;

  return Math.round((completedFields / totalFields) * 100);
}

/**
 * Check if influencer profile is complete
 * Based on validation requirements from InfluencerOnboarding.js
 */
export function isInfluencerProfileComplete(profile) {
  if (!profile) return false;

  const missingFields = getMissingInfluencerFields(profile);
  return missingFields.length === 0;
}

/**
 * Check if brand profile is complete
 * Based on validation requirements from BrandOnboarding.js
 */
export function isBrandProfileComplete(profile) {
  if (!profile) return false;

  const missingFields = getMissingBrandFields(profile);
  return missingFields.length === 0;
}
